import { useState, useEffect, useMemo, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { auctionsLabels } from "@/i18n";
import { useListing } from "@/hooks/useListing";
import { useAuth } from "@/hooks/useAuth";
import { ListingOverview, ListingMyListings } from "@/types/listing";
import { MyAuctionsCards } from "./MyAuctionsCards";
import { sellerContractNeedsSignature } from "@/lib/sellerContractStatus";
import { MyAuctionsSortDropdown } from "./MyAuctionsSortDropdown";
import { MyAuctionsFilterTabs } from "./MyAuctionsFilterTabs";
import { Tabs } from "@/components/ui/tabs";
import { Pagination } from "@/components/ui/pagination";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";

interface MyAuctionsViewProps {
  type: "active" | "completed";
}

type MyAuctionsLabels = {
  loading?: string;
  error?: string;
  tryAgain?: string;
  showing?: string;
  empty?: { active?: string; completed?: string };
};

export const MyAuctionsView = ({ type }: MyAuctionsViewProps) => {
  const { language } = useLanguage();
  const ma = (auctionsLabels(language).myAuctions ?? {}) as MyAuctionsLabels;
  const [sortBy, setSortBy] = useState<string>("updated");
  const [filterBy, setFilterBy] = useState<string>("all");
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "pageSize:myAuctions",
    5,
    [5, 10, 15, 20],
  );
  const { user } = useAuth();
  const { getMyListings } = useListing();

  // Fetch runs when tab (filterBy) or page/limit/status change – server does filtering
  const listingTypeParam =
    filterBy === "all"
      ? undefined
      : (filterBy as "auction" | "direct-purchase");
  const {
    data: myListingsData,
    isLoading: isLoadingMyListings,
    error: myListingsError,
    refetch: refetchMyListings,
  } = getMyListings({
    page: currentPage,
    limit: itemsPerPage,
    status: type === "active" ? "active" : "completed",
    listingType: listingTypeParam,
  });

  const userListings: ListingMyListings[] = useMemo(
    () => myListingsData?.listings || [],
    [myListingsData?.listings],
  );
  const totalPages = Math.ceil(
    (myListingsData?.totalCount || 0) / itemsPerPage,
  );

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  }, []);

  // Filter listings based on type (active/completed)
  const filteredListings = useMemo(() => {
    return userListings.filter((listing) => {
      const isCompleted =
        listing.status === "completed" ||
        listing.status === "reserved" ||
        listing.status === "cancelled" ||
        listing.status === "ended";
      return type === "active" ? !isCompleted : isCompleted;
    });
  }, [userListings, type]);

  // Server already filters by listingType; we only filter by status (active/completed) and sort
  const sortedListings = useMemo(() => {
    return [...filteredListings].sort((a, b) => {
      // Sold listings that need the seller's contract signature always come first
      const aNeeds = sellerContractNeedsSignature(a) ? 1 : 0;
      const bNeeds = sellerContractNeedsSignature(b) ? 1 : 0;
      if (aNeeds !== bNeeds) return bNeeds - aNeeds;

      switch (sortBy) {
        case "price-high-low": {
          const aPrice =
            type === "active"
              ? a.bidding?.highestBid ||
                a.auctionDetails?.startingPrice ||
                a.directPurchaseDetails?.currentPrice ||
                0
              : a.bidding?.highestBid ||
                a.auctionDetails?.startingPrice ||
                a.directPurchaseDetails?.currentPrice ||
                0;
          const bPrice =
            type === "active"
              ? b.bidding?.highestBid ||
                b.auctionDetails?.startingPrice ||
                b.directPurchaseDetails?.currentPrice ||
                0
              : b.bidding?.highestBid ||
                b.auctionDetails?.startingPrice ||
                b.directPurchaseDetails?.currentPrice ||
                0;
          return bPrice - aPrice;
        }
        case "price-low-high": {
          const aPriceLow =
            type === "active"
              ? a.bidding?.highestBid ||
                a.auctionDetails?.startingPrice ||
                a.directPurchaseDetails?.currentPrice ||
                0
              : a.bidding?.highestBid ||
                a.auctionDetails?.startingPrice ||
                a.directPurchaseDetails?.currentPrice ||
                0;
          const bPriceLow =
            type === "active"
              ? b.bidding?.highestBid ||
                b.auctionDetails?.startingPrice ||
                b.directPurchaseDetails?.currentPrice ||
                0
              : b.bidding?.highestBid ||
                b.auctionDetails?.startingPrice ||
                b.directPurchaseDetails?.currentPrice ||
                0;
          return aPriceLow - bPriceLow;
        }
        case "newest":
          return (
            new Date(b.createdAt || 0).getTime() -
            new Date(a.createdAt || 0).getTime()
          );
        case "oldest":
          return (
            new Date(a.createdAt || 0).getTime() -
            new Date(b.createdAt || 0).getTime()
          );
        default:
          return 0;
      }
    });
  }, [filteredListings, sortBy, type]);

  const renderListingCard = useCallback(
    (listing: ListingMyListings, index: number) => (
      <MyAuctionsCards
        listings={[listing]}
        selectedVehicle={selectedVehicle}
        setSelectedVehicle={setSelectedVehicle}
      />
    ),
    [selectedVehicle, setSelectedVehicle, type],
  );

  const handleTabChange = useCallback((value: string) => {
    setFilterBy(value);
    setCurrentPage(1);
  }, []);

  if (isLoadingMyListings) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">{ma.loading}</div>
      </div>
    );
  }

  if (myListingsError) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-red-500">
          {ma.error}
          <button
            onClick={() => refetchMyListings()}
            className="ml-2 underline"
          >
            {ma.tryAgain}
          </button>
        </div>
      </div>
    );
  }

  return (
    <Tabs value={filterBy} onValueChange={handleTabChange} className="w-full">
      <div className="flex flex-col items-center gap-3 mb-3 md:flex-row md:justify-between md:items-center">
        <MyAuctionsFilterTabs />
        <MyAuctionsSortDropdown
          sortBy={sortBy}
          setSortBy={setSortBy}
          type={type === "completed" ? "sold" : "active"}
        />
      </div>

      <div className="flex justify-center md:justify-start items-center mb-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {ma.showing
            ?.replace("{count}", String(sortedListings.length))
            .replace("{total}", String(myListingsData?.totalCount || 0))}
        </div>
      </div>

      {sortedListings.length > 0 ? (
        <div className="space-y-4">
          {sortedListings.map((listing, index) =>
            renderListingCard(listing, index),
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-lg text-gray-500 dark:text-gray-400">
            {type === "active" ? ma.empty.active : ma.empty.completed}
          </div>
        </div>
      )}

      {/* Pagination */}
      {userListings.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={myListingsData?.totalCount || 0}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[5, 10, 15, 20]}
        />
      )}
    </Tabs>
  );
};
